import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Rocket, BadgeCheck, Users, Activity, TrendingUp, Clock, ArrowUpRight } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border hairline bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-neutral-500"><Icon size={14} /><span className="label-eyebrow" style={{ fontSize: "0.55rem" }}>{label}</span></div>
    <div className="font-display text-3xl mt-2 leading-none">{value}</div>
    {sub && <div className="text-xs text-neutral-400 mt-1">{sub}</div>}
  </div>
);

const STATUS = {
  active_trial: { label: "Active trial", cls: "border border-[color:var(--brand-primary)] text-[var(--brand-primary)]" },
  expired_trial: { label: "Expired", cls: "bg-[#8B2E2E] text-white" },
  paid: { label: "Paid", cls: "bg-[var(--brand-primary)] text-white" },
  permanent: { label: "Permanent", cls: "border border-neutral-300 text-neutral-500" },
};

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});

  const load = () => api.get("/admin/analytics").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const convert = async (row) => {
    if (!window.confirm(`Convert "${row.name}" to a permanent paid workspace? This removes its trial expiry.`)) return;
    setBusy(b => ({ ...b, [row.id]: true }));
    try {
      await api.post(`/workspaces/${row.id}/convert`);
      toast.success(`${row.name} converted to paid`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(b => ({ ...b, [row.id]: false }));
    }
  };

  if (!data) return <div className="text-sm text-neutral-500">Loading analytics…</div>;
  const t = data.totals, c = data.conversion;

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Building2} label="Workspaces" value={t.workspaces} sub={`${t.permanent} permanent · ${t.paid} paid`} testid="kpi-workspaces" />
        <Kpi icon={Rocket} label="Active trials" value={t.active_trials} sub={`${t.expired_trials} expired`} testid="kpi-active-trials" />
        <Kpi icon={TrendingUp} label="Conversion rate" value={`${c.rate}%`} sub={`${c.converted}/${c.trials_started} trials converted`} testid="kpi-conversion" />
        <Kpi icon={Users} label="Trainees" value={t.trainees} sub={`${t.active_trainees_7d} active users (7d)`} testid="kpi-trainees" />
      </div>

      {data.expiring_soon.length > 0 && (
        <div className="border-2 border-[#8B2E2E] bg-[#8B2E2E]/5 p-5" data-testid="expiring-soon">
          <div className="label-eyebrow text-[#8B2E2E] mb-2 flex items-center gap-1"><Clock size={12} /> Trials expiring within 3 days</div>
          <div className="flex flex-wrap gap-2">
            {data.expiring_soon.map(e => (
              <span key={e.slug} className="text-sm border hairline bg-white px-3 py-1">{e.name} · <span className="font-mono">{e.days_left}d</span></span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="label-eyebrow mb-3 flex items-center gap-1"><Activity size={12} /> Per-workspace engagement</div>
        <div className="border hairline bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 border-b hairline">
                <th className="px-4 py-3 font-normal">Workspace</th>
                <th className="px-4 py-3 font-normal">Status</th>
                <th className="px-4 py-3 font-normal">Trainees</th>
                <th className="px-4 py-3 font-normal">Avg completion</th>
                <th className="px-4 py-3 font-normal">Active (7d)</th>
                <th className="px-4 py-3 font-normal">Trial left</th>
                <th className="px-4 py-3 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {data.workspaces.map(w => (
                <tr key={w.id} className="border-b hairline last:border-0" data-testid={`analytics-row-${w.slug}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{w.name}</div>
                    <div className="font-mono text-xs text-neutral-400">/{w.slug}{w.industry ? ` · ${w.industry}` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${STATUS[w.status]?.cls}`}>{STATUS[w.status]?.label}</span>
                  </td>
                  <td className="px-4 py-3 font-mono">{w.trainees}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="progress-hairline w-24"><div className="fill" style={{ transform: `scaleX(${(w.avg_completion || 0) / 100})` }} /></div>
                      <span className="font-mono text-xs">{w.avg_completion}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono">{w.active_7d}</td>
                  <td className="px-4 py-3 font-mono">{w.days_left != null ? `${w.days_left}d` : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {(w.status === "active_trial" || w.status === "expired_trial") && (
                      <button disabled={busy[w.id]} className="btn-primary text-xs inline-flex items-center gap-1" onClick={() => convert(w)} data-testid={`convert-btn-${w.slug}`}>
                        <BadgeCheck size={12} /> {busy[w.id] ? "…" : "Convert to paid"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
